import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash/';
import classNames from 'classnames';
import Image from '@nerdwallet/react-image';

import { Button, Title, Box, Text } from '@nerdwallet/currency';

import {
  boolCheckboxSpec,
  textColorPicker,
  subtitleInput,
  stringSelectSpec,
  stringInputSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';
import classColor from '../../lib/parse-color';

import BackgroundColor from '../background-color/background-color';
import PlayIcon from './play-icon';

import './hero-video.less';

const INCLUDE_OVERLAY_OPTIONS = {
  NONE: 'none',
  OVERLAY_VIDEO: 'overlayVideo',
  OVERLAY_IMAGE: 'overlayImage',
};

const VIDEO_TREATMENT_OPTIONS = {
  NONE: 'none',
  SHADOW: 'shadow',
  BORDER: 'border',
};

const subtitleEnum = {
  2: 'default',
  3: 'large',
};

const HeroVideo = ({
  backgroundSize,
  backgroundSizeMobile,
  backgroundImageSrcDesktop,
  backgroundImageSrcTablet,
  backgroundImageSrcMobile,
  backgroundPositionX,
  backgroundPositionY,
  bgOptions,
  borderColorTreatment,
  borderSizeTreatment,
  buttonCopy,
  buttonMinWidth,
  buttonUrl,
  includeOverlay,
  overlayImageUrl,
  overlayVideoUrl,
  overlayVideoMaxHeightDesktop,
  overlayVideoMaxHeightMobile,
  overlayVideoMaxHeightTablet,
  textColor,
  title,
  shadowColorTreatment,
  subtitle,
  subtitleMobile,
  subtitleSize,
  videoUrl,
  videoAutoplay,
  videoControls,
  videoLoop,
  videoMuted,
  videoTrack,
  videoTrackKind,
  videoMaxHeightDesktop,
  videoMaxHeightMobile,
  videoMaxHeightTablet,
  videoTreatment,
  size,
}) => {
  const [showOverlay, setShowOverlay] = useState(true);
  const playerRef = useRef(null);
  const closeOverlay = () => {
    setShowOverlay(false);
  };

  useEffect(() => {
    if (!showOverlay && playerRef) {
      playerRef.current.play();
    }
  }, [showOverlay, playerRef]);

  const heroImageSrcMap = {
    lg: backgroundImageSrcDesktop,
    md: backgroundImageSrcTablet,
    sm: backgroundImageSrcMobile,
  };

  const backgroundSizeMarkup =
    size === 'sm' && backgroundSizeMobile
      ? `${backgroundSizeMobile}`
      : `${backgroundSize}`;

  const videoMaxHeightMap = {
    lg: videoMaxHeightDesktop,
    md: videoMaxHeightTablet,
    sm: videoMaxHeightMobile,
  };

  const overlayVideoMaxHeightMap = {
    lg: overlayVideoMaxHeightDesktop,
    md: overlayVideoMaxHeightTablet,
    sm: overlayVideoMaxHeightMobile,
  };

  const borderTreatment =
    videoTreatment === VIDEO_TREATMENT_OPTIONS.BORDER
      ? {
          border: `${borderSizeTreatment} solid ${borderColorTreatment}`,
        }
      : '';

  const shadowTreatment =
    videoTreatment === VIDEO_TREATMENT_OPTIONS.SHADOW
      ? {
          boxShadow: `0px 4px 25px ${shadowColorTreatment}`,
        }
      : '';

  const videoMaxHeight =
    size && !!videoMaxHeightMap[size] ? videoMaxHeightMap[size] : null;

  const overlayVideoMaxHeight =
    size && !!overlayVideoMaxHeightMap[size]
      ? overlayVideoMaxHeightMap[size]
      : null;

  const videoWidth = videoMaxHeight && overlayVideoMaxHeight ? 'auto' : '100%';
  const displayVideo = (
    /* eslint-disable-next-line jsx-a11y/media-has-caption */
    <video
      className={classNames(['video-hero__video'])}
      width="100%"
      height="100%"
      autoPlay={videoAutoplay}
      controls={videoControls}
      loop={videoLoop}
      muted={videoMuted}
      ref={playerRef}
      key={videoUrl}
      style={{
        maxHeight: videoMaxHeight,
        width: videoWidth,
        ...borderTreatment,
        ...shadowTreatment,
      }}
    >
      <source src={videoUrl} type="video/mp4" />
      {videoTrack && (
        <track
          src={videoTrack}
          kind={videoTrackKind}
          srcLang="en"
          label="English"
        />
      )}
    </video>
  );

  const displayOverlayVideo = (
    <Box className={classNames(['video-hero__overlay--cont'])}>
      <button
        className={classNames(['video-hero__play-btn'])}
        onClick={closeOverlay}
        type="button"
        aria-label="Play"
      >
        <PlayIcon />
      </button>
      <video
        className={classNames(['video-hero__overlay-video'])}
        width="100%"
        height="100%"
        autoPlay
        loop={false}
        muted
        playsInline
        key={overlayVideoUrl}
        style={{
          width: videoWidth,
          maxHeight: overlayVideoMaxHeight,
          ...borderTreatment,
          ...shadowTreatment,
        }}
      >
        <source src={overlayVideoUrl} type="video/mp4" />
      </video>
    </Box>
  );

  const displayOverlayImage = (
    <Box className={classNames(['video-hero__overlay--cont'])}>
      <button
        className={classNames(['video-hero__play-btn'])}
        onClick={closeOverlay}
        type="button"
        aria-label="Play"
      >
        <PlayIcon />
      </button>
      <picture
        className={classNames(['video-hero__overlay-image'])}
        width="100%"
        height="100%"
        key={overlayImageUrl}
        style={{
          ...borderTreatment,
          ...shadowTreatment,
        }}
      >
        {overlayImageUrl && <Image src={overlayImageUrl} alt={title} />}
      </picture>
    </Box>
  );

  const displayVideoWithOverlay = showOverlay
    ? displayOverlayVideo
    : displayVideo;

  const displayImageWithOverlay = showOverlay
    ? displayOverlayImage
    : displayVideo;

  const mobileBackgroundPosition =
    backgroundPositionX &&
    backgroundPositionY &&
    size === 'sm' &&
    `${backgroundPositionY}-${backgroundPositionX}`;

  let selectedPreviewOption = displayVideo;

  if (includeOverlay === INCLUDE_OVERLAY_OPTIONS.OVERLAY_VIDEO) {
    selectedPreviewOption = displayVideoWithOverlay;
  }

  if (includeOverlay === INCLUDE_OVERLAY_OPTIONS.OVERLAY_IMAGE) {
    selectedPreviewOption = displayImageWithOverlay;
  }

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <div
          className={classNames([
            'video-hero__wrapper',
            mobileBackgroundPosition,
          ])}
          style={{
            backgroundColor,
            backgroundImage: size && `url(${heroImageSrcMap[size]})`,
            backgroundSize: backgroundSizeMarkup,
          }}
        >
          <Box className={classNames(['video-hero__content'])}>
            <Box className={classNames(['video-hero__copy'])}>
              <Title
                className={classNames([
                  'video-hero__title',
                  classColor(textColor),
                ])}
              >
                {title}
              </Title>
              <Box
                className={classNames([
                  'video-hero__subtitle',
                  classColor(textColor),
                ])}
              >
                <Text
                  size={
                    (size === 'sm' && 'default') || subtitleEnum[subtitleSize]
                  }
                >
                  {(size === 'sm' && subtitleMobile) || subtitle}
                </Text>
              </Box>
              {buttonUrl && buttonCopy && (
                <Box className={classNames(['video-hero__button--container'])}>
                  <Button
                    className={classNames(['video-hero__button'])}
                    href={buttonUrl}
                    primary
                    style={{ minWidth: `${buttonMinWidth}` }}
                  >
                    {buttonCopy}
                  </Button>
                </Box>
              )}
            </Box>
            <Box className={classNames(['video-hero__video-outer--container'])}>
              <Box className={classNames(['video-hero__video--container'])}>
                {selectedPreviewOption}
              </Box>
            </Box>
          </Box>
        </div>
      )}
    </BackgroundColor>
  );
};

HeroVideo.propTypes = {
  children: PropTypes.node,
  ...BackgroundColor.propTypes,
  backgroundSize: PropTypes.oneOf(['contain', 'cover', 'auto']),
  backgroundSizeMobile: PropTypes.oneOf(['contain', 'cover', 'auto']),
  backgroundImageSrcDesktop: PropTypes.string,
  backgroundImageSrcTablet: PropTypes.string,
  backgroundImageSrcMobile: PropTypes.string,
  backgroundPositionX: PropTypes.oneOf(['left', 'right', 'center']),
  backgroundPositionY: PropTypes.oneOf(['top', 'bottom', 'center']),
  borderColorTreatment: PropTypes.string,
  borderSizeTreatment: PropTypes.string,
  buttonCopy: PropTypes.string,
  buttonMinWidth: PropTypes.string,
  buttonUrl: PropTypes.string,
  includeOverlay: PropTypes.oneOf(Object.values(INCLUDE_OVERLAY_OPTIONS)),
  size: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleMobile: PropTypes.string,
  subtitleSize: PropTypes.oneOf(['2', '3']),
  textColor: PropTypes.string,
  title: PropTypes.string,
  overlayVideoUrl: PropTypes.string,
  overlayVideoMaxHeightDesktop: PropTypes.string,
  overlayVideoMaxHeightMobile: PropTypes.string,
  overlayVideoMaxHeightTablet: PropTypes.string,
  shadowColorTreatment: PropTypes.string,
  videoAutoplay: PropTypes.bool,
  videoControls: PropTypes.bool,
  videoLoop: PropTypes.bool,
  videoMuted: PropTypes.bool,
  videoTrackKind: PropTypes.string,
  videoTrack: PropTypes.string,
  videoUrl: PropTypes.string,
  videoMaxHeightDesktop: PropTypes.string,
  videoMaxHeightMobile: PropTypes.string,
  videoMaxHeightTablet: PropTypes.string,
  videoTreatment: PropTypes.oneOf(Object.values(VIDEO_TREATMENT_OPTIONS)),
};

HeroVideo.defaultProps = {
  children: null,
  ...BackgroundColor.defaultProps,
  backgroundSize: 'contain',
  backgroundSizeMobile: 'contain',
  backgroundImageSrcDesktop: '',
  backgroundImageSrcTablet: '',
  backgroundImageSrcMobile: '',
  backgroundPositionX: 'left',
  backgroundPositionY: 'top',
  borderColorTreatment: '#D8D9DA',
  borderSizeTreatment: '1px',
  buttonCopy: 'click here',
  buttonMinWidth: '',
  includeOverlay: INCLUDE_OVERLAY_OPTIONS.NONE,
  overlayVideoUrl: '',
  shadowColorTreatment: '#D8D9DA',
  size: '',
  subtitle:
    'Description (desktop) goes here. Mobile will default to this if no mobile subtitle provided.',
  subtitleSize: '2',
  title: 'Title goes here',
  videoAutoplay: false,
  videoControls: false,
  videoLoop: false,
  videoMuted: false,
  videoTrackKind: 'subtitles',
  videoTrack: '',
  videoUrl: '',
  videoTreatment: VIDEO_TREATMENT_OPTIONS.NONE,
};

HeroVideo.getEditingConfig = (componentDocument) => {
  const overlayVideoOptions =
    _.get(componentDocument, 'props.includeOverlay', '') ===
    INCLUDE_OVERLAY_OPTIONS.OVERLAY_VIDEO
      ? [
          stringInputSpec(
            'overlayVideoUrl',
            'URL of overlay mp4 video to display'
          ),
          stringInputSpec(
            'overlayVideoMaxHeightDesktop',
            'Desktop: maximum height of overlay video (in px, eg: "200px" - leave blank for 100%)',
            {
              help: 'Specify the maximum height of the overlay video viewed in desktop viewports, in pixels. eg: 200px (you must also set maximum height of video to the same value)',
            }
          ),
          stringInputSpec(
            'overlayVideoMaxHeightTablet',
            'Tablet: maximum height of overlay video (in px, eg: "200px" - leave blank for 100%)',
            {
              help: 'Specify the maximum height of the overlay video viewed in tablet viewports, in pixels. eg: 200px (you must also set maximum height of video to the same value)',
            }
          ),
          stringInputSpec(
            'overlayVideoMaxHeightMobile',
            'Mobile: maximum height of overlay video (in px, eg: "200px" - leave blank for 100%)',
            {
              help: 'Specify the maximum height of the overlay video viewed in mobile viewports, in pixels. eg: 200px (you must also set maximum height of video to the same value)',
            }
          ),
        ]
      : '';
  const overlayImageOptions =
    _.get(componentDocument, 'props.includeOverlay', '') ===
    INCLUDE_OVERLAY_OPTIONS.OVERLAY_IMAGE
      ? [stringInputSpec('overlayImageUrl', 'URL of overlay image to display')]
      : '';
  const videoTreatmentShadowOptions =
    _.get(componentDocument, 'props.videoTreatment', '') ===
    VIDEO_TREATMENT_OPTIONS.SHADOW
      ? [
          stringInputSpec(
            'shadowColorTreatment',
            'Video Drop Shadow Color Hex (eg: "#fff" - default is #D8D9DA)'
          ),
        ]
      : '';
  const videoTreatmentBorderOptions =
    _.get(componentDocument, 'props.videoTreatment', '') ===
    VIDEO_TREATMENT_OPTIONS.BORDER
      ? [
          stringInputSpec(
            'borderColorTreatment',
            'Video Border Color Hex (eg: "#fff" - default is #D8D9DA)'
          ),
          stringInputSpec(
            'borderSizeTreatment',
            'Video Border Width (in px, eg: "2px" - default is 1px)'
          ),
        ]
      : '';
  return [
    ...BackgroundColor.getEditingConfig(componentDocument),
    stringInputSpec(
      'backgroundImageSrcDesktop',
      'Background Image URL (Desktop)'
    ),
    stringInputSpec(
      'backgroundImageSrcTablet',
      'Background Image URL (Tablet)'
    ),
    stringInputSpec(
      'backgroundImageSrcMobile',
      'Background Image URL (Mobile)'
    ),
    stringSelectSpec(
      'backgroundSize',
      'Background Size',
      [
        { label: 'Contain', value: 'contain' },
        { label: 'Cover', value: 'cover' },
        { label: 'Auto', value: 'auto' },
      ],
      {
        help: 'Only applies to Desktop and Tablet',
      }
    ),
    stringSelectSpec(
      'backgroundSizeMobile',
      'Mobile Background Size',
      [
        { label: 'Contain', value: 'contain' },
        { label: 'Cover', value: 'cover' },
        { label: 'Auto', value: 'auto' },
      ],
      {
        help: 'Only applies to Mobile',
      }
    ),
    stringSelectSpec(
      'backgroundPositionY',
      'Mobile Background Vertical Position',
      [
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
        { label: 'Center', value: 'center' },
      ],
      {
        help: 'Set background horizontal position for mobile',
      }
    ),
    stringSelectSpec(
      'backgroundPositionX',
      'Mobile Background Horizontal Position',
      [
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
        { label: 'Center', value: 'center' },
      ],
      {
        help: 'Set background horizontal position for mobile',
      }
    ),
    textColorPicker,
    stringTextAreaSpec('title', 'Title'),
    subtitleInput,
    stringTextAreaSpec('subtitleMobile', 'Mobile subtitle'),
    stringSelectSpec(
      'subtitleSize',
      'Subtitle Text Size',
      [
        { label: 'Medium (default)', value: '2' },
        { label: 'Large', value: '3' },
      ],
      {
        help: 'Select the size of the subtitle text',
      }
    ),
    stringInputSpec('buttonCopy', 'Button copy', {
      help: 'Include copy for Button',
    }),
    stringInputSpec('buttonUrl', 'Button URL', {
      help: 'Include the URL for the Button link',
    }),
    stringInputSpec('buttonMinWidth', 'Minimum Button width', {
      help: 'Specify a minimum button width value (in px, eg: "200px" - leave blank for auto width)',
    }),

    stringInputSpec('videoUrl', 'MP4 video URL'),
    boolCheckboxSpec('videoAutoplay', 'Autoplay video on page load?', {
      help: 'WARNING: We advise against making use of this feature unless you really need it. (note: autoplay will not occur on mobile devices)',
    }),
    boolCheckboxSpec('videoControls', 'Show video controls?'),
    boolCheckboxSpec('videoLoop', 'Play video on loop?'),
    boolCheckboxSpec('videoMuted', 'Mute video audio?'),
    stringInputSpec('videoTrack', 'MP4 video text track URL', {
      help: 'Specify the URL for a text track associated with this video. Please note all text tracks will default to English.',
    }),
    stringSelectSpec(
      'videoTrackKind',
      'Track kind',
      [
        {
          value: 'subtitles',
          label: 'subtitles',
        },
        {
          value: 'captions',
          label: 'captions',
        },
        {
          value: 'chapters',
          label: 'chapters',
        },
        {
          value: 'descriptions',
          label: 'descriptions',
        },
        {
          value: 'metadata',
          label: 'metadata',
        },
      ],
      {
        help: 'Specifies the kind of text track',
      }
    ),
    stringInputSpec(
      'videoMaxHeightDesktop',
      'Desktop: maximum height of video (in px, eg: "200px" - leave blank for 100%)',
      {
        help: 'Specify the maximum height of the video player viewed in desktop viewports, in pixels. eg: 200px',
      }
    ),
    stringInputSpec(
      'videoMaxHeightTablet',
      'Tablet: maximum height of video (in px, eg: "200px" - leave blank for 100%)',
      {
        help: 'Specify the maximum height of the video player viewed in tablet viewports, in pixels. eg: 200px',
      }
    ),
    stringInputSpec(
      'videoMaxHeightMobile',
      'Mobile: maximum height of video (in px, eg: "200px" - leave blank for 100%)',
      {
        help: 'Specify the maximum height of the video player viewed in mobile viewports, in pixels. eg: 200px',
      }
    ),
    stringSelectSpec(
      'includeOverlay',
      'Include preview image or video overlay?',
      [
        { label: 'None', value: INCLUDE_OVERLAY_OPTIONS.NONE },
        {
          label: 'Preview video',
          value: INCLUDE_OVERLAY_OPTIONS.OVERLAY_VIDEO,
        },
        {
          label: 'Preview image',
          value: INCLUDE_OVERLAY_OPTIONS.OVERLAY_IMAGE,
        },
      ],
      {
        help: 'Select this option if you wish to include an overlay play button and looping preview video or an image.',
      }
    ),
    ...overlayVideoOptions,
    ...overlayImageOptions,
    stringSelectSpec(
      'videoTreatment',
      'Video Treatment',
      [
        { label: 'None', value: VIDEO_TREATMENT_OPTIONS.NONE },
        { label: 'Border', value: VIDEO_TREATMENT_OPTIONS.BORDER },
        { label: 'Drop Shadow', value: VIDEO_TREATMENT_OPTIONS.SHADOW },
      ],
      {
        help: 'Select the styling of the video outer container',
      }
    ),
    ...videoTreatmentBorderOptions,
    ...videoTreatmentShadowOptions,
  ];
};

HeroVideo.displayName = 'Hero Video';

export default connect(
  createStructuredSelector({
    size: _.property('mediaQuery'),
  })
)(HeroVideo);
